// =============================================================================
// ManagerEditDrawer.jsx
//
// Drawer that allows a Manager to edit any user's submission.
// Reuses the same ActivityCard / TextItem primitives as WeeklyUpdateForm,
// but targets the PUT /ManagerEdit endpoint instead of POST /Save.
//
// Props:
//   open          boolean            — controls drawer visibility
//   submission    object | null      — pre-loaded submission (from apiGetSubmissionById)
//   loading       boolean            — true while fetching the submission
//   onClose       () => void
//   onSaved       (updatedSub) => void  — called after a successful save
// =============================================================================

import React, { useState, useEffect } from 'react';
import {
    Drawer, Box, Typography, Stack, Divider, CircularProgress,
    Button, IconButton, TextField, MenuItem, Select,
    FormControl, InputLabel, Alert, Chip,
} from '@mui/material';
import {
    Close as CloseIcon,
    Add as AddIcon,
    Clear as RemoveIcon,
    Save as SaveIcon,
    FlashOn as PresentIcon,
    AutoGraph as NextIcon,
    ReportProblem as IssueIcon,
    Assignment as AobIcon,
    Edit as EditIcon,
} from '@mui/icons-material';

import { apiManagerEdit } from '../requests/weeklyUpdateRequests';

// ── Config ────────────────────────────────────────────────────────────────────
const TECHNOLOGIES = ['2G', '4G', '5G'];
const VENDORS      = ['Nokia', 'Ericsson', 'Other'];

const emptyActivity = () => ({ technology: '', vendor: '', description: '' });
const emptyTextItem = () => ({ description: '' });

// ── Activity card (edit mode) ─────────────────────────────────────────────────
const ActivityCard = ({ item, index, onUpdate, onRemove }) => (
    <Box sx={{
        position: 'relative', mb: 1.5, p: 2,
        borderRadius: 2, bgcolor: 'background.paper',
        border: '1px solid', borderColor: 'divider',
    }}>
        <Stack spacing={1.5}>
            <Stack direction="row" spacing={1}>
                <FormControl fullWidth size="small">
                    <InputLabel>Tech</InputLabel>
                    <Select
                        value={item.technology || ''}
                        label="Tech"
                        onChange={(e) => onUpdate(index, 'technology', e.target.value)}
                    >
                        {TECHNOLOGIES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                    </Select>
                </FormControl>
                <FormControl fullWidth size="small" sx={{ minWidth: 130 }}>
                    <InputLabel>Vendor</InputLabel>
                    <Select
                        value={item.vendor || ''}
                        label="Vendor"
                        onChange={(e) => onUpdate(index, 'vendor', e.target.value)}
                    >
                        {VENDORS.map(v => <MenuItem key={v} value={v}>{v}</MenuItem>)}
                    </Select>
                </FormControl>
            </Stack>
            <TextField
                fullWidth multiline minRows={2} maxRows={6}
                placeholder="Describe activity..."
                value={item.description || ''}
                onChange={(e) => onUpdate(index, 'description', e.target.value)}
                size="small"
            />
        </Stack>
        <IconButton
            size="small"
            onClick={() => onRemove(index)}
            sx={{
                position: 'absolute', top: 6, right: 6,
                bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider',
                width: 22, height: 22,
                '&:hover': { color: 'error.main' },
            }}
        >
            <RemoveIcon sx={{ fontSize: 14 }} />
        </IconButton>
    </Box>
);

// ── Text item row ─────────────────────────────────────────────────────────────
const TextItemRow = ({ item, index, onUpdate, onRemove, placeholder }) => (
    <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ mb: 1 }}>
        <TextField
            fullWidth multiline minRows={1} maxRows={4}
            size="small"
            placeholder={placeholder}
            value={item.description || ''}
            onChange={(e) => onUpdate(index, e.target.value)}
        />
        <IconButton size="small" onClick={() => onRemove(index)} sx={{ mt: 0.5, flexShrink: 0 }}>
            <RemoveIcon sx={{ fontSize: 16, color: 'error.main' }} />
        </IconButton>
    </Stack>
);

// ── Section header ────────────────────────────────────────────────────────────
const SectionHeader = ({ icon, label, color = 'primary', onAdd }) => (
    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
        <Stack direction="row" spacing={1} alignItems="center">
            {icon}
            <Typography variant="subtitle2" fontWeight={800} color={color} sx={{ letterSpacing: 0.5 }}>
                {label}
            </Typography>
        </Stack>
        <Button size="small" startIcon={<AddIcon />} onClick={onAdd} variant="outlined" sx={{ py: 0.3 }}>
            Add
        </Button>
    </Stack>
);

// ── Main component ────────────────────────────────────────────────────────────
const ManagerEditDrawer = ({ open, submission, loading, onClose, onSaved }) => {
    const [presentWeek, setPresentWeek] = useState([emptyActivity()]);
    const [nextWeek,    setNextWeek]    = useState([emptyActivity()]);
    const [issues,      setIssues]      = useState([emptyTextItem()]);
    const [aob,         setAob]         = useState([emptyTextItem()]);
    const [saving,      setSaving]      = useState(false);
    const [error,       setError]       = useState(null);

    // Populate form when submission loads
    useEffect(() => {
        if (!submission) return;
        setPresentWeek(submission.presentWeekActivities?.length > 0 ? submission.presentWeekActivities : [emptyActivity()]);
        setNextWeek(submission.nextWeekActivities?.length     > 0 ? submission.nextWeekActivities    : [emptyActivity()]);
        setIssues(submission.issues?.length                   > 0 ? submission.issues                : [emptyTextItem()]);
        setAob(submission.aob?.length                         > 0 ? submission.aob                   : [emptyTextItem()]);
        setError(null);
    }, [submission]);

    // ── Activities helpers ────────────────────────────────────────────────────
    const updateActivity = (list, setList) => (index, field, value) =>
        setList(prev => prev.map((it, i) => i === index ? { ...it, [field]: value } : it));

    const removeActivity = (list, setList) => (index) =>
        setList(prev => prev.length === 1 ? [emptyActivity()] : prev.filter((_, i) => i !== index));

    // ── Text item helpers ─────────────────────────────────────────────────────
    const updateText = (setList) => (index, value) =>
        setList(prev => prev.map((it, i) => i === index ? { description: value } : it));

    const removeText = (list, setList) => (index) =>
        setList(prev => prev.length === 1 ? [emptyTextItem()] : prev.filter((_, i) => i !== index));

    // ── Save ──────────────────────────────────────────────────────────────────
    const handleSave = async () => {
        if (!submission) return;
        setSaving(true);
        setError(null);

        const clean = (arr) => arr.filter(it => it.description?.trim());

        try {
            await apiManagerEdit({
                submissionId:          submission.id,
                presentWeekActivities: clean(presentWeek),
                nextWeekActivities:    clean(nextWeek),
                issues:                clean(issues),
                aob:                   clean(aob),
            });

            // Return merged submission to parent so the dashboard card refreshes
            onSaved({
                ...submission,
                presentWeekActivities: clean(presentWeek),
                nextWeekActivities:    clean(nextWeek),
                issues:                clean(issues),
                aob:                   clean(aob),
            });
        } catch (err) {
            setError(err?.response?.data?.message || 'Failed to save. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={onClose}
            PaperProps={{ sx: { width: { xs: '100%', sm: 580 }, display: 'flex', flexDirection: 'column' } }}
        >
            {/* ── Header ── */}
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start"
                sx={{ px: 3, py: 2.5, borderBottom: '1px solid', borderColor: 'divider', flexShrink: 0 }}>
                <Box>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <EditIcon fontSize="small" color="primary" />
                        <Typography variant="h6" fontWeight={800}>Edit Submission</Typography>
                    </Stack>
                    {submission && (
                        <Typography variant="caption" color="text.secondary">
                            {submission.userName} — Week {submission.weekNumber}/{submission.year}
                        </Typography>
                    )}
                </Box>
                <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
            </Stack>

            {/* ── Body ── */}
            <Box sx={{ flex: 1, overflowY: 'auto', px: 3, py: 2.5 }}>
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
                        <CircularProgress />
                    </Box>
                ) : !submission ? (
                    <Alert severity="error">Could not load submission.</Alert>
                ) : (
                    <Stack spacing={4}>
                        {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}

                        {/* PRESENT WEEK */}
                        <Box>
                            <SectionHeader
                                icon={<PresentIcon fontSize="small" color="primary" />}
                                label="PRESENT WEEK"
                                onAdd={() => setPresentWeek(prev => [...prev, emptyActivity()])}
                            />
                            {presentWeek.map((item, i) => (
                                <ActivityCard
                                    key={i} item={item} index={i}
                                    onUpdate={updateActivity(presentWeek, setPresentWeek)}
                                    onRemove={removeActivity(presentWeek, setPresentWeek)}
                                />
                            ))}
                        </Box>

                        <Divider />

                        {/* NEXT WEEK */}
                        <Box>
                            <SectionHeader
                                icon={<NextIcon fontSize="small" color="primary" />}
                                label="NEXT WEEK"
                                onAdd={() => setNextWeek(prev => [...prev, emptyActivity()])}
                            />
                            {nextWeek.map((item, i) => (
                                <ActivityCard
                                    key={i} item={item} index={i}
                                    onUpdate={updateActivity(nextWeek, setNextWeek)}
                                    onRemove={removeActivity(nextWeek, setNextWeek)}
                                />
                            ))}
                        </Box>

                        <Divider />

                        {/* ISSUES */}
                        <Box>
                            <SectionHeader
                                icon={<IssueIcon fontSize="small" color="error" />}
                                label="ISSUES & RISKS"
                                color="error.main"
                                onAdd={() => setIssues(prev => [...prev, emptyTextItem()])}
                            />
                            {issues.map((item, i) => (
                                <TextItemRow
                                    key={i} item={item} index={i}
                                    onUpdate={updateText(setIssues)}
                                    onRemove={removeText(issues, setIssues)}
                                    placeholder="Describe issue or risk..."
                                />
                            ))}
                        </Box>

                        <Divider />

                        {/* AOB */}
                        <Box>
                            <SectionHeader
                                icon={<AobIcon fontSize="small" color="action" />}
                                label="ANY OTHER BUSINESS"
                                color="text.secondary"
                                onAdd={() => setAob(prev => [...prev, emptyTextItem()])}
                            />
                            {aob.map((item, i) => (
                                <TextItemRow
                                    key={i} item={item} index={i}
                                    onUpdate={updateText(setAob)}
                                    onRemove={removeText(aob, setAob)}
                                    placeholder="Any other business..."
                                />
                            ))}
                        </Box>
                    </Stack>
                )}
            </Box>

            {/* ── Footer ── */}
            {!loading && submission && (
                <Stack direction="row" justifyContent="flex-end" spacing={2}
                    sx={{ px: 3, py: 2, borderTop: '1px solid', borderColor: 'divider', flexShrink: 0 }}>
                    <Button onClick={onClose} disabled={saving}>Cancel</Button>
                    <Button
                        variant="contained" startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                        onClick={handleSave} disabled={saving}
                    >
                        {saving ? 'Saving...' : 'Save Changes'}
                    </Button>
                </Stack>
            )}
        </Drawer>
    );
};

export default ManagerEditDrawer;